'use client';
import { useForkRef } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

import CloseCircleIcon from '../../icons/CloseCircle';
import SearchIcon from '../../icons/Search';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';
import TextField from '../TextField';

export const styles = {
  iconSx: ({ idsTokens = idsTokensDefault }) => ({
    color: idsTokens.textField.default.color.input.enabled,
    '.MuiFormControl-root:hover &': {
      color: idsTokens.textField.default.color.input.hover,
    },
    '.MuiFormControl-root:focus-within &': {
      color: idsTokens.textField.default.color.input.focus,
    },
    '.MuiFormControl-root:has(input:active) &': {
      color: idsTokens.textField.default.color.input.pressed,
    },
    '.MuiInputBase-root.Mui-disabled &': {
      color: idsTokens.textField.default.color.input.disabled,
    },
  }),
  clearButtonSx: {
    '.MuiInputBase-root:not(:hover) &': {
      display: 'none',
    },
  },
};

export const Search = ({ onChange, IconButtonProps, inputRef, forwardedRef: ref, ...rest }) => {
  const localRef = React.createRef();
  const forkRef = useForkRef(localRef, inputRef);

  const handleClear = (e) => {
    IconButtonProps?.onClick && IconButtonProps.onClick(e);

    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    ).set;
    nativeInputValueSetter.call(localRef.current, '');
    const event = new Event('input', { bubbles: true });
    localRef.current.dispatchEvent(event);
  };

  return (
    <TextField
      icon={<CloseCircleIcon />}
      IconButtonProps={{
        ...IconButtonProps,
        onClick: handleClear,
        sx: [
          styles.clearButtonSx,
          ...(Array.isArray(IconButtonProps?.sx) ? IconButtonProps?.sx : [IconButtonProps?.sx]),
        ],
      }}
      startIcon={<SearchIcon sx={styles.iconSx} />}
      onChange={onChange}
      inputRef={forkRef}
      {...rest}
      ref={ref}
    />
  );
};

Search.propTypes = {
  /** If `true`, the input element is focused during the first mount. */
  autoFocus: PropTypes.bool,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** If `true`, the component takes up the full width of its container. */
  fullWidth: PropTypes.bool,
  /** The label content. */
  label: PropTypes.node,
  /** Callback fired when the value changes. */
  onChange: PropTypes.func,
  /** The short hint displayed in the input before the user enters a value. */
  placeholder: PropTypes.string,
  /** If `true`, a leading icon is added to the helper text. */
  showHelperTextIcon: PropTypes.bool,
  /** The size of the component. */
  size: PropTypes.oneOf(['small', 'medium']),
  /** The value of the input element, required for a controlled component. */
  value: PropTypes.any,
};

export default withRef()(Search);
